/**
 * reachability — CAN WE ACTUALLY REACH THE SERVER? (BUG-2026-07-14-VC559)
 *
 * `navigator.onLine` is a LINK-LAYER hint owned by the OS. It answers "does this
 * device believe it has a network interface", NOT "can this app reach its API".
 * It latches false across a VPN/interface flap and a PWA resume, and it is
 * false-negative behind captive portals. On 2026-07-14 the field app read it as
 * FALSE on a demonstrably live network — a GET returned 200 twenty-two ms later —
 * and took the offline branch, so the visit-report POST was never issued and
 * eleven days of rep data went into a queue that never drained.
 *
 * So: the hint may TRIGGER a probe. Only the PROBE may assert offline.
 *
 * THE PROBE'S LAW — ANY HTTP RESPONSE PROVES REACHABILITY.
 * 200, 401, 404, 503 — and 301 — all prove the network carried a request to a
 * server and carried an answer back. ONLY a transport rejection or a timeout
 * proves the network is dead. Checking for `res.ok` here would classify our own
 * backend's 5xx as "the device is offline" — which is precisely how a dead write
 * path got misdiagnosed as a connectivity problem for eleven days.
 *
 * 2026-07-16 — THE PROBE BROKE ITS OWN LAW, AND IT COST ANOTHER 13 DAYS.
 * The law above says we do not care WHAT the server answers, only THAT it
 * answers. The code then went and cared: it probed the BARE base `/service`,
 * which prod 301s to an unroutable `http://…:8080` host, and — having never set
 * `redirect` — fetch's default 'follow' CHASED that answer into a black hole.
 * The 5s abort fired, the AbortError landed in the catch, and `probeReachable`
 * returned false on a network that had answered in ~100ms (measured 92–144ms
 * across five runs against prod on 2026-07-16). `queueFlush()` then
 * early-returned `skipped:'offline-confirmed'`, so a real rep's visit reports sat
 * undelivered in IndexedDB for 13 days. The server ANSWERED; we just refused to
 * count it. Both cures below are independent, and both are load-bearing:
 *   1. `redirect:'manual'` — count the answer, never chase it. No future redirect
 *      on any target can re-brick the probe.
 *   2. probe a REAL SL route (`/v1/health`), not the bare base — so a green probe
 *      proves the SERVICE LAYER is reachable, not merely that an edge answered.
 * The lesson is narrow and worth keeping: widening what counts as an ANSWER is
 * correct; widening what counts as REACHABLE is not. A transport rejection and a
 * timeout must still read as offline — a rep in a real dead zone depends on it.
 *
 * Solved art, taken as-is: this is the standard captive-portal / connectivity
 * check (a cheap unconditional HEAD, no-store, short timeout, AbortController).
 */

import { needsConfirmationProbe, recordProbeVerdict, resetSustainedVerdict } from './reachabilitySustained'

// The banner's confirmation rule is part of this module's public surface.
export {
  SUSTAINED_UNREACHABLE_PROBES,
  getConsecutiveProbeFailures,
  isSustainedUnreachable,
  onSustainedUnreachableChange,
} from './reachabilitySustained'

/** `unknown` = we have not asked yet, and we do NOT get to guess (D-33 R3). */
export type Reachability = 'reachable' | 'unreachable' | 'unknown'

export interface ProbeOptions {
  /**
   * The EXACT probe target, used verbatim (the cache-buster is still appended).
   * Defaults to the service-layer health endpoint — see `defaultProbeUrl`.
   */
  url?: string
  /** Abort budget. A probe that cannot answer quickly is a probe that failed. */
  timeoutMs?: number
  /** Injected for tests + for callers that must not touch the global. */
  fetchImpl?: typeof fetch
}

const PROBE_TIMEOUT_MS = 5_000
const MONITOR_INTERVAL_MS = 30_000

/** Delay of the confirmation re-probe after a first failure (see reachabilitySustained.ts). */
const CONFIRM_REPROBE_DELAY_MS = 5_000

/**
 * A REAL Service-Layer route (declared in the SL openapi spec), reached through
 * the configured base. Probing an actual SL endpoint is strictly more meaningful
 * than probing the base: the base only proves the EDGE (Google Frontend) is up,
 * while `/v1/health` proves the SERVICE LAYER ITSELF answered — measured on prod,
 * its 401 body is SL's own error envelope, carrying SL's `request_id`.
 *
 * It needs no auth: the probe sends `credentials:'omit'` deliberately, and a 401
 * is a perfectly good answer under the probe's law.
 */
const PROBE_PATH = '/v1/health'

/** Same-origin default — the value prod actually ships (`VITE_SERVICE_LAYER_BASE_URL=/service`). */
const DEFAULT_SERVICE_BASE = '/service'

/** Exactly one slash between base and path, whether or not the base carries one. */
function joinBaseAndPath(base: string, path: string): string {
  return `${base.replace(/\/+$/, '')}${path}`
}

/**
 * The probe target: `<service-layer base>/v1/health`, derived from config
 * (D-33 R4) — never a hardcoded URL.
 *
 * ⛔ NEVER PROBE THE BARE BASE. Measured against prod on 2026-07-16:
 *   HEAD https://portal.clanda.de/service?_probe=… → 301
 *        location: http://portal.clanda.de:8080/service/?_probe=…
 * Google Frontend issues a trailing-slash redirect for the bare base and builds
 * the Location from the CONTAINER'S INTERNAL scheme+port — `http` (mixed-content
 * from an HTTPS page) and `:8080` (not routable from a browser). The bare base
 * was the ONLY broken URL on the entire surface, and this probe was its ONLY
 * caller. Every real SL path answers in <250ms.
 */
function defaultProbeUrl(): string {
  const base = import.meta.env.VITE_SERVICE_LAYER_BASE_URL
  const serviceBase = typeof base === 'string' && base.length > 0 ? base : DEFAULT_SERVICE_BASE
  return joinBaseAndPath(serviceBase, PROBE_PATH)
}

/**
 * Ask the network. Resolves true when a server ANSWERED (any status), false when
 * the transport rejected or the probe timed out.
 *
 * THROWS when there is no fetch at all. That is not a network verdict — it is a
 * broken runtime, and reporting it as "offline" would be a fabricated fact
 * (D-33 R1/R3): it would silently route every write into the queue forever.
 */
export async function probeReachable(options: ProbeOptions = {}): Promise<boolean> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch
  if (typeof fetchImpl !== 'function') {
    throw new Error('reachability: no fetch implementation available — cannot determine reachability')
  }

  const url = options.url ?? defaultProbeUrl()
  const controller = new AbortController()
  const timer = setTimeout(() => { controller.abort() }, options.timeoutMs ?? PROBE_TIMEOUT_MS)

  try {
    // Cache-busted so a stale 200 from the HTTP cache can never fake reachability.
    await fetchImpl(`${url}${url.includes('?') ? '&' : '?'}_probe=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'omit',
      // COUNT the redirect, do not CHASE it. A 3xx is an HTTP RESPONSE — under
      // this file's own law it already proves the network carried a request to a
      // server and carried an answer back, so there is nothing left to learn by
      // following it. `redirect:'manual'` resolves an opaque-redirect response
      // instead of chasing the Location; fetch's DEFAULT is 'follow', and that
      // default is what broke this probe on prod (see `defaultProbeUrl`): it
      // chased a 301 into an unroutable host, hung until the abort fired, and
      // reported a HEALTHY network as offline — freezing the queue for 13 days.
      // This is defence-in-depth with the target fix: no future redirect on any
      // target can ever re-brick the probe.
      redirect: 'manual',
      signal: controller.signal,
    })
    return true
  } catch (err) {
    // The transport rejected (or we aborted it). THIS IS THE VERDICT, not a
    // swallowed error — an unreachable network is exactly what we came to find
    // out. It is recorded so a caller can say WHY, never hidden.
    lastProbeError = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
    return false
  } finally {
    clearTimeout(timer)
  }
}

// ── The monitor: one cached verdict the whole app reads ────────────────────────

let state: Reachability = 'unknown'
let lastProbeError: string | null = null
let listeners: Array<(state: Reachability) => void> = []
let timer: ReturnType<typeof setInterval> | null = null
let boundHint: (() => void) | null = null
let confirmTimer: ReturnType<typeof setTimeout> | null = null

/** The last transport error a probe saw — so an offline claim can be explained. */
export function getLastProbeError(): string | null {
  return lastProbeError
}

/** The current cached verdict. `unknown` until a probe has actually run. */
export function getReachability(): Reachability {
  return state
}

/**
 * TRUE only when a PROBE actually failed. Deliberately false on `unknown`: with no
 * evidence we stay OPTIMISTIC and attempt the write, because "attempt and queue on
 * real failure" cannot be wrong about the network, while "guess and maybe attempt"
 * just was — for eleven days.
 */
export function isConfirmedOffline(): boolean {
  return state === 'unreachable'
}

/** Subscribe to reachability TRANSITIONS. Returns an unsubscribe fn. */
export function onReachabilityChange(listener: (state: Reachability) => void): () => void {
  listeners.push(listener)
  return () => { listeners = listeners.filter(l => l !== listener) }
}

function setState(next: Reachability): void {
  if (next === state) return
  state = next
  for (const listener of listeners) listener(next)
}

/** Run a probe and publish the verdict. The ONLY way the cached state changes. */
export async function refreshReachability(options: ProbeOptions = {}): Promise<Reachability> {
  const reachable = await probeReachable(options)
  setState(reachable ? 'reachable' : 'unreachable')
  recordProbeVerdict(reachable)
  return state
}

/**
 * Start the background reachability loop: on an interval, and on the events that
 * make a stale verdict likely — a tab/PWA resume (`visibilitychange`) and the OS
 * link-layer hint flipping in EITHER direction. The hint is only ever a TRIGGER to
 * go and ask; it never sets the state itself.
 */
export function startReachabilityMonitor(options: ProbeOptions = {}): void {
  if (timer) return // already running

  const probe = (): void => {
    // A rejected probe promise means a BROKEN RUNTIME (no fetch), not an offline
    // network — it must not be swallowed into a false "offline" verdict.
    void refreshReachability(options)
      .then(() => {
        // A FIRST failure is not yet sustained: confirm it with one independent
        // re-probe soon, rather than leaving the verdict unconfirmed for a full
        // interval. The banner waits for this second failure.
        if (needsConfirmationProbe() && confirmTimer === null && timer !== null) {
          confirmTimer = setTimeout(() => {
            confirmTimer = null
            probe()
          }, CONFIRM_REPROBE_DELAY_MS)
        }
      })
      .catch((err: unknown) => {
        console.error('[reachability] probe could not run — connectivity is UNKNOWN, not offline', err)
      })
  }

  boundHint = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    probe()
  }

  window.addEventListener('online', boundHint)
  window.addEventListener('offline', boundHint)
  document.addEventListener('visibilitychange', boundHint)

  timer = setInterval(probe, MONITOR_INTERVAL_MS)
  probe()
}

/** Stop the loop and reset the verdict to `unknown` (no stale claim survives it). */
export function stopReachabilityMonitor(): void {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  if (confirmTimer) {
    clearTimeout(confirmTimer)
    confirmTimer = null
  }
  if (boundHint) {
    window.removeEventListener('online', boundHint)
    window.removeEventListener('offline', boundHint)
    document.removeEventListener('visibilitychange', boundHint)
    boundHint = null
  }
  state = 'unknown'
  lastProbeError = null
  listeners = []
  resetSustainedVerdict()
}
